import { Material } from "three";
import { getFramePositions, getFrameScaling } from "shared/utils/door";
import { frameSettings as FrameSettings } from "shared/utils/doorConfigurations";
import { BeamVertical } from "../components/BeamVertical";
import { getGeometryBounds2D } from "../components/openingGeometry";
import { SchuifGLTFResult } from "../schuif/SchuifType";
import type { VerticalBeamPosition } from "entities/configurator/model/layoutMeta";
import { standingDoorBeamInsetMeters } from "entities/configurator/model/layoutGeometry";
import { getLocalVerticalBeamX } from "./verticalLayoutUtils";

export const SchuifVerticalLayout = ({
  nodes,
  material,
  width,
  height,
  semanticPosition,
  frameSettings,
}: {
  nodes: SchuifGLTFResult["nodes"];
  material: Material;
  width: number;
  height: number;
  semanticPosition: VerticalBeamPosition | undefined;
  frameSettings?: FrameSettings;
}) => {
  const isVisible = semanticPosition !== undefined;
  const positions = getFramePositions(width, height);
  const scaling = getFrameScaling(width, height);
  const leftBounds = getGeometryBounds2D(nodes.frame_left.geometry);
  const rightBounds = getGeometryBounds2D(nodes.frame_right.geometry);
  const topBounds = getGeometryBounds2D(nodes.frame_top.geometry);
  const bottomBounds = getGeometryBounds2D(nodes.frame_bottom.geometry);
  const beamBounds = getGeometryBounds2D(nodes.beam_vertical.geometry);
  const scaleX = frameSettings?.generalScaleX ?? 1;
  const left =
    leftBounds.minX *
      (scaling.vertical.x + (frameSettings?.frameLeft?.scaleX ?? 0)) +
    positions.left.x +
    (frameSettings?.frameLeft?.shiftX ?? 0);
  const right =
    rightBounds.maxX *
      (scaling.vertical.x + (frameSettings?.frameRight?.scaleX ?? 0)) +
    positions.right.x +
    (frameSettings?.frameRight?.shiftX ?? 0);
  const top =
    topBounds.minY * (frameSettings?.frameTop?.scaleY ?? scaling.horizontal.y) +
    positions.top.y +
    (frameSettings?.frameTop?.shiftY ?? 0);
  const bottom = bottomBounds.maxY + positions.bottom.y;
  const beamX = isVisible
    ? getLocalVerticalBeamX(semanticPosition, left, right, beamBounds, {
        insetMeters: standingDoorBeamInsetMeters,
        parentScaleX: scaleX,
      })
    : 0;

  return (
    <group scale-x={scaleX}>
      <BeamVertical
        visible={isVisible}
        geometry={nodes.beam_vertical.geometry}
        material={material}
        x={beamX}
        bottom={bottom}
        top={top}
        scaleX={1 / scaleX}
      />
    </group>
  );
};
